/**
 * NotificationsCard — simple topic × channel toggles on
 * /settings/account (moved off /profile per PROD-27).
 *
 * Consumer-focused design. The backend still tracks 20+ fine-grained
 * `NotificationCategory` values (one per event), but the UI groups
 * them into four topics that map cleanly to what parents / chefs /
 * school staff actually care about. Toggling a topic's channel flips
 * every underlying category in one batch.
 *
 * Channels shown: Email + Text. In-app (the bell) is always on; push
 * folds into Text for the consumer view (we don't surface the iOS push
 * cert until it's wired). Internal-flavored notifications (RFP bids,
 * deal-stage changes, ticket-style task assignment) are NOT exposed
 * here — those bleed in from other apps and don't belong in a school
 * meal app. The backend still respects defaults for them; we just
 * don't give the user a toggle.
 *
 * Topics + visibility:
 *   - Orders                always shown
 *   - Billing               shown when Billing is in nav
 *   - Family                shown when My Family is in nav (PARENT)
 *   - Account               always shown (system + account-level alerts)
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { toast } from '@ordo/ui/sonner'
import { Switch } from '@ordo/ui/switch'
import { useMemo } from 'react'

import { type MeProfileNotificationPreference, useUpdateMyNotificationPreferenceMutation } from '@/api/me'
import { NOTIFICATION_DEFAULTS, type NotificationCategory } from '@/api/notifications'

// Two user-controllable channels. Email is its own column; Text covers
// SMS + Push (most users don't distinguish, and the push cert isn't
// wired yet). The backend still tracks SMS and PUSH separately —
// toggling Text flips both in one batch.
type UiChannel = 'EMAIL' | 'TEXT'

const UI_CHANNEL_LABELS: Record<UiChannel, string> = {
  EMAIL: 'Email',
  TEXT: 'Text',
}

interface NotificationTopic {
  id: 'orders' | 'billing' | 'family' | 'account'
  label: string
  description: string
  /** Underlying NotificationCategory values this topic covers. */
  categories: NotificationCategory[]
  /** Nav slug that gates visibility (omit for "always shown"). */
  gatedBySlug?: string
}

const TOPICS: readonly NotificationTopic[] = [
  {
    id: 'orders',
    label: 'Orders',
    description: 'Confirmations, reminders, cutoffs, receipts, and dish changes.',
    categories: [
      'ORDER_CONFIRMED',
      'ORDER_FAILED',
      'ORDER_REMINDER',
      'ORDER_CUTOFF_SOON',
      'ORDER_RECEIPT',
      'ORDER_ITEM_REPLACED',
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    description: 'Payments, low balance, refunds, and account changes.',
    categories: ['PAYMENT_FAILED', 'PAYMENT_RECEIVED', 'ACCOUNT_LOW_BALANCE', 'ACCOUNT_INVITATION', 'BALANCE_CLAWBACK'],
    gatedBySlug: 'billing',
  },
  {
    id: 'family',
    label: 'Family',
    description: 'When a child is served or a meal is missed.',
    categories: ['CHILD_SERVED_MEAL', 'CHILD_UNSERVED_FLAGGED'],
    gatedBySlug: 'my-family',
  },
  {
    id: 'account',
    label: 'Account',
    description: 'Important announcements from Ordo.',
    categories: ['SYSTEM_ANNOUNCEMENT'],
  },
]

/** Resolve which underlying NotificationChannel(s) a UI channel maps to. */
function underlyingChannels(ui: UiChannel): Array<'EMAIL' | 'SMS' | 'PUSH'> {
  return ui === 'EMAIL' ? ['EMAIL'] : ['SMS', 'PUSH']
}

export interface NotificationsCardProps {
  /** Saved preference rows; missing (category, channel) pairs fall back to NOTIFICATION_DEFAULTS. */
  preferences: MeProfileNotificationPreference[]
  /** Nav slugs the current user can see — gates per-topic visibility. */
  visibleNavSlugs: ReadonlySet<string>
}

export function NotificationsCard({ preferences, visibleNavSlugs }: NotificationsCardProps) {
  const updateMutation = useUpdateMyNotificationPreferenceMutation()

  const prefMap = useMemo(() => {
    const out = new Map<string, MeProfileNotificationPreference>()
    for (const pref of preferences) {
      out.set(`${pref.category}|${pref.channel}`, pref)
    }
    return out
  }, [preferences])

  const visibleTopics = useMemo(
    () => TOPICS.filter((t) => !t.gatedBySlug || visibleNavSlugs.has(t.gatedBySlug)),
    [visibleNavSlugs],
  )

  // A topic's channel is ON when ANY underlying (category, channel) pair
  // is on. Predictable + matches how a user mentally collapses 5 row
  // toggles into 1 "Orders" toggle.
  function isTopicChannelOn(topic: NotificationTopic, ui: UiChannel): boolean {
    for (const cat of topic.categories) {
      for (const ch of underlyingChannels(ui)) {
        const explicit = prefMap.get(`${cat}|${ch}`)
        const enabled = explicit ? explicit.enabled : NOTIFICATION_DEFAULTS[cat][ch]
        if (enabled) {
          return true
        }
      }
    }
    return false
  }

  async function toggleTopic(topic: NotificationTopic, ui: UiChannel, next: boolean) {
    // Flip every (category, channel) in the batch. We fire them in
    // parallel — if any one fails the toast surfaces the first error;
    // the user can retry by toggling again.
    const writes: Promise<unknown>[] = []
    for (const category of topic.categories) {
      for (const channel of underlyingChannels(ui)) {
        writes.push(updateMutation.mutateAsync({ category, channel, enabled: next }))
      }
    }
    try {
      await Promise.all(writes)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Try again in a moment.'
      toast.error("Couldn't save your preference", { description: message })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>Pick how we reach you. The in-app bell is always on.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="grid grid-cols-[1fr_5rem_5rem] items-center border-b border-border bg-muted/40 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <span>Topic</span>
            <span className="text-center">Email</span>
            <span className="text-center">Text</span>
          </div>
          {visibleTopics.map((topic, idx) => (
            <div
              key={topic.id}
              className={`grid grid-cols-[1fr_5rem_5rem] items-center gap-4 px-4 py-3 ${
                idx > 0 ? 'border-t border-border' : ''
              }`}>
              <div className="min-w-0">
                <p className="text-sm font-medium">{topic.label}</p>
                <p className="text-xs text-muted-foreground">{topic.description}</p>
              </div>
              {(['EMAIL', 'TEXT'] as const).map((ch) => (
                <div key={ch} className="flex justify-center">
                  <Switch
                    checked={isTopicChannelOn(topic, ch)}
                    onCheckedChange={(next) => void toggleTopic(topic, ch, next)}
                    aria-label={`${topic.label} — ${UI_CHANNEL_LABELS[ch]}`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
